import React, { useMemo, useRef, useState } from "react";
import AttendanceMobileDailyList from "./AttendanceMobileDailyList";
import AttendanceMobileFixedHeader from "./AttendanceMobileFixedHeader";
import AttendanceDailyBulkActionBar from "./AttendanceDailyBulkActionBar";
import { DESIGN_TOKENS } from "../../constants/designTokens";

export default function AttendanceMobileContent({
  activeTab,
  setActiveTab,
  bulkHoverState,
  changeDate,
  changeMonth,
  changeYear,
  currentDate,
  currentMonth,
  currentYear,
  dailyAttendance,
  dailyStats,
  filteredStudents,
  handleBulkStatusChange,
  handleClearSelection,
  handleSaveDaily,
  handleSelectAll,
  handleSelectRow,
  handleStatusChange,
  handleSyncSoteria,
  isSoteriaLoading,
  onListSwipeEnd,
  onTouchMove,
  onTouchStart,
  searchQuery,
  selectedDepartment,
  selectedIds,
  setCurrentDate,
  setCurrentMonth,
  setCurrentYear,
  setSearchQuery,
  setSortKey,
  setSortOrder,
  sortKey,
  sortOrder,
  targetGroup,
  setBulkHoverState,
  theme,
  handleTouchMove,
  handleSelectDragStart,
  handleDragOver,
  isDragging,
  setSelectedIds,
  scrollContainerRef,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileStatusFilter, setMobileStatusFilter] = useState("all");
  const [nextPendingCursor, setNextPendingCursor] = useState(0);
  const brand = DESIGN_TOKENS.brand;


  const mobileFilteredStudents = useMemo(() => {
    if (activeTab !== "daily") return filteredStudents;
    if (mobileStatusFilter === "all") return filteredStudents;
    if (mobileStatusFilter === "none") {
      return filteredStudents.filter((student) => (dailyAttendance?.[student.id] || "none") === "none");
    }
    return filteredStudents.filter((student) => (dailyAttendance?.[student.id] || "none") === mobileStatusFilter);
  }, [activeTab, dailyAttendance, filteredStudents, mobileStatusFilter]);

  const pendingStudents = useMemo(
    () => filteredStudents.filter((student) => (dailyAttendance?.[student.id] || "none") === "none"),
    [dailyAttendance, filteredStudents]
  );
  const hasPending = dailyStats.none > 0;

  const handleJumpNextPending = () => {
    if (!pendingStudents.length || !scrollContainerRef.current) return;
    const target = pendingStudents[nextPendingCursor % pendingStudents.length];
    const targetEl = scrollContainerRef.current.querySelector(`[data-attendance-row-id="${target.id}"]`);
    if (targetEl) {
      targetEl.scrollIntoView({ block: "center", behavior: "smooth" });
      setNextPendingCursor((prev) => (prev + 1) % pendingStudents.length);
    }
  };

  return (
    <div
      className="relative w-full h-full flex flex-col overflow-hidden bg-[#F0F0EB]"
      onTouchStart={onTouchStart}
      onTouchMove={(e) => {
        onTouchMove?.(e);
        handleTouchMove?.(e);
      }}
      onTouchEnd={onListSwipeEnd}
    >
      <AttendanceMobileFixedHeader
        activeTab={activeTab}
        bulkHoverState={bulkHoverState}
        changeDate={changeDate}
        changeMonth={changeMonth}
        changeYear={changeYear}
        currentDate={currentDate}
        currentMonth={currentMonth}
        currentYear={currentYear}
        dailyStats={dailyStats}
        filteredCount={filteredStudents.length}
        handleBulkStatusChange={handleBulkStatusChange}
        handleClearSelection={handleClearSelection}
        handleSelectAll={handleSelectAll}
        handleSyncSoteria={handleSyncSoteria}
        isSoteriaLoading={isSoteriaLoading}
        selectedIds={selectedIds}
        setCurrentDate={setCurrentDate}
        setCurrentMonth={setCurrentMonth}
        setCurrentYear={setCurrentYear}
        setSearchQuery={setSearchQuery}
        setSortKey={setSortKey}
        setSortOrder={setSortOrder}
        sortKey={sortKey}
        sortOrder={sortOrder}
        searchQuery={searchQuery}
        targetGroup={targetGroup}
        theme={theme}
        isScrolled={isScrolled}
      />

      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto relative select-none custom-scrollbar-mobile"
        onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 4)}
      >
        {activeTab === "daily" && (
          <div className="px-2.5 pt-1.5 pb-1 flex items-center gap-1.5 overflow-x-auto">
            {[
              { key: "all", label: "전체" },
              { key: "none", label: "미처리" },
              { key: "late", label: "지각" },
              { key: "absent", label: "결석" },
            ].map((option) => (
              <button
                key={option.key}
                onClick={() => setMobileStatusFilter(option.key)}
                className={`h-7 px-2.5 rounded-full text-[11px] font-bold border whitespace-nowrap ${mobileStatusFilter === option.key
                  ? `${brand.primaryBgClass} text-white ${brand.primaryBorderSolidClass}`
                  : "bg-white text-gray-600 border-gray-200"
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === "daily" && (
          <AttendanceMobileDailyList
            dailyAttendance={dailyAttendance}
            filteredStudents={mobileFilteredStudents}
            handleSelectRow={handleSelectRow}
            handleStatusChange={handleStatusChange}
            selectedIds={selectedIds}
            targetGroup={targetGroup}
            selectedDepartment={selectedDepartment}
            handleSelectDragStart={handleSelectDragStart}
            handleDragOver={handleDragOver}
            isDragging={isDragging}
          />
        )}

        {(activeTab === "monthly" || activeTab === "yearly") && (
          <div className="p-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-4 text-center text-gray-600 text-sm">
              <p className="font-bold text-gray-700">모바일에서는 일일 출석에 최적화되어 있어요.</p>
              <p className="mt-1 text-xs text-gray-500">
                월간/연간 상세는 데스크톱에서 확인할 수 있어요.
              </p>
              <button
                onClick={() => setActiveTab("daily")}
                className="mt-3 h-9 px-3 rounded-xl bg-[#354F38] text-white text-xs font-bold"
              >
                일일 출석으로 이동
              </button>
            </div>
          </div>
        )}
      </div>

      {activeTab === "daily" && (
        <div
          className="fixed left-0 right-0 z-40"
          style={{ bottom: "calc(88px + env(safe-area-inset-bottom, 0px))" }}
        >
          <AttendanceDailyBulkActionBar
            bulkHoverState={bulkHoverState}
            filteredCount={filteredStudents.length}
            handleBulkStatusChange={handleBulkStatusChange}
            handleClearSelection={handleClearSelection}
            handleSaveDaily={handleSaveDaily}
            handleSelectAll={handleSelectAll}
            handleSyncSoteria={handleSyncSoteria}
            isMobile={true}
            isSoteriaLoading={isSoteriaLoading}
            selectedCount={selectedIds.length}
            setBulkHoverState={setBulkHoverState}
            theme={theme}
          />
        </div>
      )}
    </div>
  );
}
